import { AdScriptFbF } from '@modules/AdScript/Fb/F/Function/module/AdScriptFbF';
import { AdScriptGaF } from '@modules/AdScript/Ga/F/Function/module/AdScriptGaF';
import { AdScriptKakaoF } from '@modules/AdScript/Kakao/F/Function/module/AdScriptKakaoF';
import { AdScriptNaverF } from '@modules/AdScript/Naver/F/Function/module/AdScriptNaverF';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { UserJwtF } from '@modules/User/Jwt/F/Function/module/UserJwtF';
import { AuthorizationTypeS } from '@services/MarppleShop/features/Authorization/outbound/share';
import { BaseLayoutOptions } from '@services/MarppleShop/features/Core/service';
import { MPS_INVITE_TYPE } from '@services/MarppleShop/features/Creator/type/invite.ts';
import { MarppleShopTextLogoLarge } from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { LayoutView } from '@services/MarppleShop/renderApp/components/cells/Layout/LayoutView';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';
import { MPS_CUSTOMER_CENTER_NUMBER } from '@services/MarppleShop/shared/constant';
import { font } from '@services/MarppleShop/shared/typography';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import { html, Page, View } from 'rune-ts';
import klass from './LoginResultPage.module.scss';

export interface LoginResultPageData extends Partial<AuthorizationTypeS.LoginResponseBody> {
  url?: string;
  seller_code?: string;
  is_signup: boolean;
  is_sns_connect: boolean;
  is_error?: boolean;
  creator_invite_type?: MPS_INVITE_TYPE;
  creator_invite_error?: boolean;
  creator_invite_msg?: string;
  creator_invite_title?: string;
  creator_invite_email?: string;
  layout_options: BaseLayoutOptions;

  // 스토리북에서 화면 확인을 위한 플래그입니다. onRender 를 작동시키지 않습니다.
  sb_test?: boolean;
}

class LoginResultBodyView extends View<LoginResultPageData> {
  protected override template() {
    const { is_signup, is_sns_connect, is_error, creator_invite_type } = this.data;
    const is_apply_creator =
      creator_invite_type === 'APPLY_SELLER' ||
      creator_invite_type === 'APPLY_SELLER_HAS_STORE' ||
      creator_invite_type === 'INVITE_CREW_SELLER';

    if (is_error && is_apply_creator) {
      return this.#applyCreatorErrorTemplate();
    }

    if (is_error) {
      return this.#normalErrorTemplate();
    }

    if (is_signup || is_sns_connect) {
      return this.#welcomeTemplate();
    }

    return this.#normalTemplate();
  }

  #normalTemplate() {
    const is_mobile = getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.container}">
        <div class="${klass.wrapper}" data-gap="${is_mobile ? 16 : 48}">
          <div class="${klass.logo}">
            <img
              src="//s3.marpple.co/files/u_3040850/2024/9/original/d7b3f1e932fc164e1bbe41959ef919537bc56f1c1.gif"
              alt="loading"
            />
          </div>
          <div class="${klass.wrapper}" data-gap="8">
            <div class="${font('24_bold', '20_bold')} ${klass.title}">
              ${ET('mps2::login::loading_title')}
            </div>
            <div class="${font('16_medium', '14_medium')} ${klass.guide}">
              ${html.preventEscape(ET('mps2::login::loading_guide'))}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  #welcomeTemplate() {
    const { is_sns_connect } = this.data;
    const is_mobile = getSharedData(this)?.is_mobile;

    return html`<div class="${klass.container}">
      <div class="${klass.logo}">${MarppleShopTextLogoLarge}</div>
      <div class="${klass.wrapper}" data-gap="${is_mobile ? 16 : 48}">
        <div class="${klass.congrats}">
          <img
            src="${getResizedUrl({
              url: '//s3.marpple.co/files/u_2283830/2024/4/original/57c0f4dbb3b454bf021c71464c18e0a94c5346861.png',
              format: 'webp',
              width: 900,
              quality: 80,
            })}"
            alt="congrats"
          />
        </div>
        <div class="${klass.wrapper}" data-gap="8">
          <div class="${font('24_bold', '20_bold')}">${ET('mps2::signup::welcome')}</div>
          <div class="${font('16_medium', '14_medium')} ${klass.guide}">
            ${html.preventEscape(
              ET('mps2::signup::welcome_message', { context: is_sns_connect ? 'sns_connect' : undefined }),
            )}
          </div>
        </div>
      </div>
    </div>`;
  }

  #normalErrorTemplate() {
    const { creator_invite_error, creator_invite_title, creator_invite_msg } = this.data;
    const is_mobile = getSharedData(this)?.is_mobile;

    return html` <div class="${klass.container}">
      <div class="${klass.wrapper}" data-gap="${is_mobile ? 16 : 48}">
        <div class="${klass.loading}">
          <img
            src="https://s3.marpple.co/files/u_3040850/2024/9/original/d7b3f1e932fc164e1bbe41959ef919537bc56f1c1.gif"
            alt="loading"
          />
        </div>
        <div class="${klass.wrapper}" data-gap="8">
          <div class="${font('24_bold', '20_bold')} ${klass.title}">
            ${creator_invite_error
              ? (creator_invite_title ?? ET('mps2::login::error_title'))
              : ET('mps2::login::error_title')}
          </div>
          <div class="${font('16_medium', '14_medium')} ${klass.guide}">
            ${html.preventEscape(
              creator_invite_error
                ? (creator_invite_msg ?? ET('mps2::login::error_guide'))
                : ET('mps2::login::error_guide'),
            )}
          </div>
          ${creator_invite_error
            ? html`<div class="${font('16_medium', '14_medium')} ${klass.cs}">
                <a href="/${ET.lang}">${ET('mps2::login::go_main')}</a>
              </div>`
            : html`
                <div class="${font('16_medium', '14_medium')} ${klass.cs}">
                  <span>
                    ${html.preventEscape(ET('mps2::login::cs'))}
                    <a href="tel://${MPS_CUSTOMER_CENTER_NUMBER[ET.lang]}"
                      >${MPS_CUSTOMER_CENTER_NUMBER[ET.lang]}</a
                    >
                  </span>
                </div>
              `}
        </div>
      </div>
    </div>`;
  }

  #applyCreatorErrorTemplate() {
    const {
      seller_code,
      url,
      creator_invite_error,
      creator_invite_title,
      creator_invite_msg,
      creator_invite_email,
    } = this.data;
    const is_mobile = getSharedData(this)?.is_mobile;

    return html` <div class="${klass.container}">
      <div class="${klass.wrapper}" data-gap="${is_mobile ? 16 : 48}">
        <div class="${klass.loading}">
          <img
            src="https://s3.marpple.co/files/u_3040850/2024/9/original/d7b3f1e932fc164e1bbe41959ef919537bc56f1c1.gif"
            alt="loading"
          />
        </div>
        <div class="${klass.wrapper}" data-gap="24">
          <div class="${klass.wrapper}" data-gap="8">
            <div class="${font('24_bold', '20_bold')} ${klass.title}">
              ${creator_invite_error
                ? (creator_invite_title ?? ET('mps2::login::error_title'))
                : ET('mps2::login::error_title')}
            </div>
            <div class="${font('16_medium', '14_medium')} ${klass.guide}">
              ${html.preventEscape(
                creator_invite_error
                  ? (creator_invite_msg ?? ET('mps2::login::error_guide'))
                  : ET('mps2::login::error_guide'),
              )}
            </div>
          </div>
          ${creator_invite_email
            ? html`<div class="${klass.email} ${font('16_bold')}">${creator_invite_email}</div>`
            : ''}
        </div>
        <div class="${klass.buttons}">
          ${new ButtonAction({
            label: ET('mps2::login::try_later'),
            href: `/${ET.lang}`,
            type: 'line',
            fit: true,
            size: 'large',
            klass: klass.second,
          })}
          ${new ButtonAction({
            label: ET('mps2::login::go_retry'),
            href: `/${ET.lang}/logout${
              seller_code && url
                ? `?url=${encodeURIComponent(`/login?url=${url}&seller_code=${seller_code}`)}`
                : ''
            }`,
            type: 'blue',
            size: 'large',
          })}
        </div>
      </div>
    </div>`;
  }
}

export class LoginResultPage extends Page<LoginResultPageData> {
  protected override template() {
    const { layout_options } = this.data;
    const layout_props = {
      body: new LoginResultBodyView(this.data),
      footer: '',
    };

    return html`
      <div>
        ${new LayoutView(layout_props, {
          require_top_arrow_button: false,
          full_width: true,
          ...layout_options,
        })}
      </div>
    `;
  }

  protected override onRender() {
    if (this.data.is_error) {
      this.delegate('click', `.${klass.go_main}`, (e) => {
        e.originalEvent.preventDefault();
        this.#handleRedirect();
      });

      return;
    }

    if (this.data.sb_test) {
      return;
    }

    this.#handleRedirect();
  }

  #handleRedirect() {
    const { is_signup, user, store, url, redirect_url, seller_code } = this.data;

    if (is_signup && user) {
      AdScriptGaF.signUpMpCreator({
        email: user.email,
      });
      AdScriptFbF.signUp();
      AdScriptKakaoF.signUp();
      AdScriptNaverF.mpsSignUp();
    }

    if (MShopUtilF.isApp()) {
      if (seller_code && redirect_url) {
        return UserJwtF.loginMessage(user, {
          sel: 'SellerStudioScreen',
          params: { uri: redirect_url },
        });
      }

      if (seller_code && store?.domain_name) {
        return UserJwtF.loginMessage(user, {
          sel: 'SellerStudioScreen',
          params: { uri: `/${T.lang}/${store?.domain_name}/settings` },
        });
      }

      if (url) {
        return UserJwtF.loginMessage(user, makeEmptyScreenNavigate(url).navigate);
      }

      return UserJwtF.loginMessage(user);
    }

    if (seller_code && redirect_url) {
      return window.location.replace(redirect_url);
    }

    if (seller_code || store?.domain_name) {
      return window.location.replace(
        store?.domain_name ? `/${ET.lang}/${store.domain_name}/settings` : `/${ET.lang}`,
      );
    }

    window.location.replace(url ?? `/${ET.lang}`);
  }
}
